import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Box,
  IconButton,
  Typography,
  Slider,
  Card,
  CircularProgress,
} from "@mui/material";
import styles from "./VideoModal.module.scss";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import CustomButton from "../custom-button/CustomButton";
import { CONTENT_STATUS_COMPLETED_ID } from "../../utility/constants/common-constants";
import { enqueueSnackbar } from "notistack";
import { useSaveContentStatusMutation } from "../custom-content-modal/SaveContentStatusAPISlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { invalidateEducationLibrary } from "../../features/education-library/services/GetEducationLibraryAPISlice";
import CustomSpinner from "../custom-spinner/CustomSpinner";
import { videoCompletionMessage } from "../../utility/functions/common-functions";
import { useTranslation } from "react-i18next";

interface VideoModalProps {
  open: boolean;
  onClose: () => void;
  videoUrl?: string;
  subtitle?: string;
  modalTitle?: string;
  modalSummarry?: string;
  casePlanMilestoneMappingId?: string;
  activityCompletionStatus?: string;
  patientProfileId?: number;
  lang?: string;
  enableVideoBuffer?: boolean;
  activityPoints?: number;
}

const VideoModal: React.FC<VideoModalProps> = ({
  open,
  onClose,
  videoUrl,
  subtitle,
  modalTitle,
  casePlanMilestoneMappingId,
  activityCompletionStatus,
  modalSummarry,
  patientProfileId,
  lang,
  enableVideoBuffer = true,
  activityPoints,
}) => {
  const dispatch = useAppDispatch();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [completedVideo, setCompletedVideo] = useState(false);
  const [volume, setVolume] = useState<number>(0.8);
  const [isLoading, setIsLoading] = useState(true);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const { t } = useTranslation();

  const patientUserName = useAppSelector(
    (state) => state.patientProfileDetails.patientProfileInfo.userName
  );

  const pendingStatuses = [
    t("inProgress"),
    t("pending"),
    t("taskPending"),
    t("notCompleted"),
    t("initiated"),
    t("notStarted"),
  ];

  const currentStatus = pendingStatuses.includes(
    activityCompletionStatus ? activityCompletionStatus : ""
  );

  useEffect(() => {
    if (!open) {
      setIsLoading(true);
    }
  }, [open]);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (event: Event, newValue: number | number[]) => {
    setVolume(newValue as number);
    if (videoRef.current) {
      videoRef.current.volume = newValue as number;
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setPlayedSeconds(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setIsLoading(false);
    }
  };

  const handleVideoEnd = () => {
    setCompletedVideo(true);
    setIsPlaying(false);
  };

  const onCloseModal = () => {
    setPlayedSeconds(0);
    setCompletedVideo(false);
    onClose();
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const [contentStatus, { isLoading: saveButtonLoading }] =
    useSaveContentStatusMutation();

  const handleCompletionClick = async () => {
    try {
      const body = {
        casePlanMilestoneMappingId: casePlanMilestoneMappingId,
        statusId: CONTENT_STATUS_COMPLETED_ID,
        updatedBy: patientUserName || "Patient",
      };

      const response = await contentStatus({ body }).unwrap();
      if (patientProfileId) {
        invalidateEducationLibrary(dispatch, patientProfileId, lang!);
      }
      onClose();
      enqueueSnackbar(response.updateStatus.value, { variant: "success" });
    } catch (err: any) {
      enqueueSnackbar("Error in saving the update", { variant: "error" });
    }
  };

  if (saveButtonLoading) {
    return <CustomSpinner />;
  }

  return (
    <Modal
      open={open}
      onClose={onCloseModal}
      aria-labelledby="video-modal-title"
      aria-describedby="video-modal-description"
      data-testid="video-modal"
    >
      <Box className={styles.modalBox}>
        <Box className={styles.videoContainer}>
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
            padding={"10px"}
          >
            <Typography variant="body2" fontWeight={"600"}>
              {modalTitle}
            </Typography>
            <Typography variant="body2">{modalSummarry}</Typography>
          </Box>
          {/* Show Loader Until Video Loads */}
          {isLoading && (
            <Box
              className={styles.loader}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <CircularProgress />
            </Box>
          )}
          {completedVideo && currentStatus ? (
            <Box>
              <Card
                sx={{
                  textAlign: "center",
                  padding: "5rem",
                  border: "1px solid #BCA685",
                  boxShadow: "none !important",
                }}
              >
                <Typography variant="h6" fontWeight="500" fontSize="20px">
                  {videoCompletionMessage(activityPoints!)}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    marginTop: "2rem",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <CustomButton
                    variant="contained"
                    color="primary"
                    onClick={handleCompletionClick}
                    text="Complete"
                  ></CustomButton>
                </Box>
              </Card>
            </Box>
          ) : (
            <video
              ref={videoRef}
              src={videoUrl}
              controls={enableVideoBuffer}
              width="100%"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleVideoEnd}
              muted={isMuted}
              autoPlay
              controlsList="nodownload"
            />
          )}
        </Box>
        <IconButton
          onClick={onCloseModal}
          aria-label="Close video modal"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "grey.500",
          }}
        >
          <CloseIcon />
        </IconButton>
        {!enableVideoBuffer && (
          <Box className={styles.bottomPanel}>
            <Box className={styles.leftControls}>
              <IconButton
                className={`control-button ${isPlaying ? "pause" : "play"}`}
                aria-label={isPlaying ? "Pause video" : "Play video"}
                onClick={handlePlayPause}
              >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
              <Box className={styles.timeDisplay}>
                <Typography variant="body2">
                  {formatTime(playedSeconds)} / {formatTime(duration)}
                </Typography>
              </Box>
            </Box>
            <Box className={styles.volumeControls}>
              <IconButton
                className={`control-button ${isMuted ? "unmute" : "mute"}`}
                onClick={handleMuteUnmute}
                aria-label={isMuted ? "Unmute video" : "Mute video"}
              >
                {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
              <Slider
                value={volume}
                sx={{ width: "100px", marginLeft: "16px", color: "#8e8e8e" }}
                onChange={handleVolumeChange}
                aria-labelledby="volume-slider"
                min={0}
                max={1}
                step={0.01}
                aria-label="Volume control"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default VideoModal;
